import { useTranslation } from 'react-i18next';
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { R1FactType } from '@/api/rest/resources/types/fact';
import { MembershipWithOrganizationTypeEnum } from '@/api/rest/resources/types/membership';
import { getXAxisDomainForHistoricGraph, minMaxAxisDomain } from '@/components/Charts/utils';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { useMembershipType } from '@/hooks/useMembershipType';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';
import { printYear } from '@/utils/formatting/date';

import { NoData } from '../../../components/NoData';
import { Tile, TileCapsuleContent, TileCta, TileTitle } from './Tile';

export const NDVITile = () => {
  const { t } = useTranslation();
  const pathSet =
    useMembershipType() === MembershipWithOrganizationTypeEnum.land_steward ? paths.landSteward : paths.buyer;

  const plotGroupId = usePlotGroupId();

  const ndviHistoricGraphDataFact = R1FactType.vegetation_condition_ndvi_historic_graph;
  const { [ndviHistoricGraphDataFact]: ndviHistoricGraphData } =
    useOrganizationIndicatorGraphFact<NDVIHistoricGraphData[]>(ndviHistoricGraphDataFact);
  const ndviHistoricGraphDataFormatted = ndviHistoricGraphData?.value
    ?.filter((d) => d.name === 'vegetation_condition_ndvi')
    // @ts-ignore typescript does not allow arithmetic operations between date objects
    .sort((pointA, pointB) => new Date(pointA.date) - new Date(pointB.date))
    .map((d) => ({ ...d, date: new Date(d.date).getTime() }));

  const ndviLatestAnalysisFact = R1FactType.vegetation_condition_ndvi;
  const { [ndviLatestAnalysisFact]: ndviLatestAnalysis } =
    useOrganizationIndicatorReportingFact<number>(ndviLatestAnalysisFact);

  const chartData = ndviHistoricGraphDataFormatted;
  let ndviLatestAnalysisDisplay = `${useDisplayNumber(ndviLatestAnalysis?.value ?? 0)}`;
  ndviLatestAnalysisDisplay = !isNullish(ndviLatestAnalysis?.value) ? ndviLatestAnalysisDisplay : '-';

  const noChartData = !chartData?.length;

  if (noChartData) {
    return <NoData />;
  }

  return (
    <Tile
      chart={<Chart chartData={chartData} />}
      title={<TileTitle>{t('shared.ncaDetail.details.ndvi.stats.header.title')}</TileTitle>}
      capsuleContent={<TileCapsuleContent>{ndviLatestAnalysisDisplay}</TileCapsuleContent>}
      cta={
        <TileCta to={buildPath(pathSet.biodiversityDetails, { queryParams: { plotGroupId } })}>
          {t('global.analysis.viewDetails')}
        </TileCta>
      }
    />
  );
};

type NDVIHistoricGraphData = {
  date: number;
  name: 'vegetation_condition_ndvi';
  value: number;
  unit: '';
};

const Chart = ({ chartData }: { chartData: NDVIHistoricGraphData[] }) => {
  const firstTick = chartData.at(0)?.date;
  const lastTick = chartData.at(-1)?.date;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const xTicksDomain = getXAxisDomainForHistoricGraph(firstTick!, lastTick!) ?? [
    new Date().getFullYear(),
    new Date().getFullYear(),
  ];
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const startYear = xTicksDomain[0]!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const endYear = xTicksDomain[1]!;
  const ticksRange = Array(endYear - startYear + 1).keys();
  const xTicks = Array.from(ticksRange).map((x) => new Date(`12-31-${startYear + x}`).getTime());
  const yearsSubset = xTicks.filter((_, i) => i % 2 !== 0 && i !== xTicks.length - 1);

  return (
    <ResponsiveContainer width='100%' height={80}>
      <LineChart
        data={chartData}
        margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
        }}
      >
        <Line isAnimationActive={false} dataKey='value' stroke='#6b9169' strokeWidth={2} dot={false} />
        <XAxis
          dataKey='date'
          scale='time'
          type='number'
          domain={['dataMin', 'dataMax']}
          ticks={yearsSubset}
          tickFormatter={printYear}
          axisLine={false}
          tickLine={false}
        />
        <YAxis hide dataKey='value' type='number' scale='sequential' width={0} domain={minMaxAxisDomain(0.8, 1.2)} />
      </LineChart>
    </ResponsiveContainer>
  );
};
